import type { PetAbilityLine, PetSheet } from "@highmarch/app";
import { icon } from "../iconsKit";
import { barHtml } from "../kit";
import { type SkinCard, swatchWell } from "../hub/skinsModel";
import type { UpgradeAction } from "../hub/queueModel";
import { nextRowHtml, upgradeBtnHtml } from "../hub/upgradeBits";
import { petPortraitHtml } from "./petArt";
import { capWords, coatWords, comeAlongWords, petStateWords, unlockWords } from "./petWords";
import { abilityWords, growth } from "./abilityWords";

/**
 * The pet sheet's own parts (Pets v2, docs/PETS.md), drawn inside the tower upgrade sheet (hub/upgradeSheet.ts
 * openPet, which adds the running job, the queue, the speed-ups and FINISH NOW exactly as for a tower): the pup's
 * portrait in its worn coat with "Lv 7 / 12" and the bar to its cap, the come-along switch, each ability's numbers now
 * and at the next level (or "Unlocks at Lv 5"), the coats it can wear (worn, caught, or "Catch a wild pup wearing
 * it"), and the next level's button — UPGRADE, QUEUE NEXT, or why not (the towers' level holds it, the max). Strings
 * only; the sheet binds `.pa-come`, `.pa-coat`, `.upgrade` and `.queuego`.
 */

/** The head's sub-line: its level against the cap. */
export function petHeadSub(s: PetSheet): string { return capWords(s); }

/** The portrait for the sheet's head tile, in the worn coat. */
export function petHeadPortrait(s: PetSheet): string { return petPortraitHtml(s.pet.id, 60); }

/** The stage under the head: the big portrait, the level bar to the cap, its state, and the come-along switch. */
export function petStageHtml(s: PetSheet): string {
  const share = s.cap > 0 ? Math.min(1, s.level / s.cap) : 1, c = comeAlongWords(s.equipped);
  return `<div class="pa-stage"><div class="pa-port">${petPortraitHtml(s.pet.id, 96)}<b class="pa-lv disp">Lv ${s.level}</b></div>`
    + `<div class="pa-facts"><p class="pa-blurb">${s.pet.blurb}</p>${barHtml(share, "gold", "thin")}<small class="pa-state">${petStateWords(s)}</small>`
    + `<button type="button" class="pa-come${s.equipped ? " on" : ""}" aria-pressed="${s.equipped}"><span class="pc-t"><b>${c.title}</b></span><span class="pc-sw" aria-hidden="true"><i></i></span></button></div></div>`;
}

/** One ability's row: its name, what it does now (and the next level's gain), or the level it unlocks at. */
export function abilityRowHtml(a: PetAbilityLine): string {
  if (!a.unlocked || !a.now) {
    return `<li class="pa-ab shut">${icon("lock", 14)}<span class="pa-at"><b>${a.name}</b><small>${unlockWords(a.fromLevel)}</small></span></li>`;
  }
  const g = growth(a);
  return `<li class="pa-ab">${icon("star", 14)}<span class="pa-at"><b>${a.name}</b><small>${abilityWords(a.now)}</small></span>${g ? `<i class="pa-up">${g}</i>` : ""}</li>`;
}

export function abilitiesHtml(s: PetSheet): string {
  return `<div class="pa-sec"><b class="disp">Abilities</b></div><ul class="pa-abs">${s.abilities.map(abilityRowHtml).join("")}</ul>`;
}

/** The coats: each a portrait in that coat (on its colours, alone until the picture lands), its name and whether it is worn or caught; a tap on a caught one wears it. */
export function coatsHtml(cards: readonly SkinCard[]): string {
  if (cards.length <= 1) return "";
  const one = (c: SkinCard): string => `<button type="button" class="pa-coat${c.worn ? " worn" : ""}${c.access.have ? "" : " lk"}" data-id="${c.id ?? ""}" aria-label="${c.look.name}: ${coatWords(c.access.have, c.worn)}">`
    + `<span class="pa-cp" style="background:${swatchWell(c.look)}">${c.art ? `<img src="${c.art}" alt="" decoding="async" onerror="this.remove()">` : ""}${c.access.have ? "" : icon("lock", 14)}</span><b>${c.look.name}</b>${c.worn ? "<small>Wearing</small>" : c.access.have ? "<small>Caught</small>" : ""}</button>`;
  return `<div class="pa-sec"><b class="disp">Coats</b></div><div class="pa-coats">${cards.map(one).join("")}</div>`;
}

/**
 * The next level and its button (queueModel.ts `upgradeAction` for the builders and the queue, the pet sheet's state
 * for the cap): UPGRADE / QUEUE NEXT with its gold and timer, or why not. `secondary`: a job or a queued level shows above.
 */
export function petNextHtml(s: PetSheet, a: UpgradeAction, seconds: number, secondary: boolean, _hint: string): string {
  const u = s.upgrade;
  if (u.state === "max" || a.kind === "max") return secondary ? "" : `<div class="next us-next us-max"><b class="disp">MAX LEVEL</b></div>`;
  if (u.state === "cap" || (a.kind !== "queueFull" && a.toLevel > s.cap)) {
    return `${nextRowHtml(a.toLevel - 1, a.toLevel, "", 0, secondary)}${secondary ? "" : `<button class="btn dark disp" disabled>AT YOUR TOWERS' LEVEL</button>`}`;
  }
  if (a.kind === "queueFull") return `${nextRowHtml(a.toLevel - 1, a.toLevel, "", 0, secondary)}${secondary ? "" : `<button class="btn dark disp" disabled>NO FREE BUILDER</button>`}`;
  return `${nextRowHtml(a.toLevel - 1, a.toLevel, "", seconds, secondary)}${upgradeBtnHtml(a.kind === "upgrade" ? "upgrade" : "queue", a.cost, a.affordable)}`;
}
